
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, Grid, Stack, CardMedia } from '@mui/material';
import useIsAddToCartVisible from './CheckCartVisible';
import { formatCurrencyNew } from 'utils/helper';
import { isMobile } from 'react-device-detect';

export default function StickyAddToCartBar({
    product = {},
    availableSizes = [],
    availableColors = [],
    selectedSizeLabel = '',
    selectedColorKey = null,
    selectedVariant = null,
    handleSizeChange = () => { },
    handleColorChange = () => { },
    handleAddToCart = () => { },
    errorSize = '',
    errorColor = '',
    isWebsiteFunctional = true,
    currentCartItem = [],
    availableSizesForSelectedColor,
    addToCartBtnId = 'add-to-cart-btn',
    websiteInfo
}) {
    const isAddToCartVisible = useIsAddToCartVisible(addToCartBtnId, selectedVariant?.id);
    if (isAddToCartVisible) return null;

    const thumb = selectedVariant?.images?.[0]?.image || product?.variants?.[0]?.images?.[0]?.image || product?.variants?.[0]?.images?.[0]?.image_url || '';
    console.log("thumb", selectedVariant)
    const stockCount = selectedVariant?.stock_count ?? product.count ?? null;
    const currentQty = currentCartItem?.[0]?.quantity || 0;
    const isDisabled = !isWebsiteFunctional || (stockCount !== null && stockCount === currentQty);

    return (
        <>
            {isMobile ? (
                <Box
                    position="fixed"
                    bottom={0}
                    left={0}
                    right={0}
                    bgcolor="#fff"
                    px={2}
                    py={2}
                    boxShadow={3}
                    zIndex={9999}
                    sx={{
                        borderTop: '1px solid rgba(0,0,0,0.08)',
                    }}
                >
                    <Grid
                        container
                        spacing={2}
                        alignItems="center"
                        sx={{
                            flexDirection: { xs: 'column', sm: 'row' },
                            textAlign: { xs: 'left', sm: 'left' },
                        }}
                    >
                        {/* Product Info */}
                        <Grid
                            item
                            xs={12}
                            sm={3}
                            md={2}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: 1,
                                width: '100%',
                            }}
                        >
                            <CardMedia
                                component="img"
                                src={thumb}
                                alt={product?.name}
                                sx={{
                                    width: { xs: 60, sm: 60 },
                                    height: 'auto',
                                    borderRadius: 1,
                                    objectFit: 'contain',
                                }}
                            />
                            <Box>
                                <Typography variant="body2" fontWeight={700} noWrap>
                                    {product?.name}
                                </Typography>
                                <Typography variant="body2">
                                    {formatCurrencyNew(
                                        product?.price || selectedVariant?.price || product?.display_price || '',
                                        websiteInfo?.currency_symbol || ''
                                    ) || ''}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Color Options */}
                        {availableColors?.length > 0 && (
                            <Grid item xs={12} sm={2} md={2} sx={{ width: '100%' }}>
                                <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    spacing={1}
                                    sx={{ overflowX: 'auto' }}
                                >
                                    {availableColors.map((color) => {
                                        const key = color.key ?? color.id ?? color.name?.toLowerCase();
                                        const isSelected = selectedColorKey === key;
                                        return (
                                            <Box
                                                key={key}
                                                onClick={() => handleColorChange(key)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        borderRadius: 2,
                                                        backgroundColor: color.hex_code,
                                                        backgroundImage: color.img
                                                            ? `url(${color.img})`
                                                            : undefined,
                                                        backgroundSize: 'cover',
                                                        border: isSelected ? '3px solid' : '1px solid',
                                                        borderColor: isSelected
                                                            ? 'rgba(25,118,210,1)'
                                                            : 'rgba(0,0,0,0.12)',
                                                    }}
                                                />
                                                <Typography variant="caption" noWrap>
                                                    {color.name}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                                {errorColor && (
                                    <Typography variant="caption" color="error">
                                        {errorColor}
                                    </Typography>
                                )}
                            </Grid>
                        )}

                        {/* Size Options */}
                        {availableSizesForSelectedColor() && (
                            <Grid item xs={12} sm={5} md={6} sx={{ width: '100%' }}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        overflowX: 'auto',
                                        px: 0.5,
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    {availableSizesForSelectedColor().map((label) => {
                                        const isActive =
                                            selectedSizeLabel?.toString().toLowerCase() ===
                                            label.toString().toLowerCase();
                                        return (
                                            <Button
                                                key={label}
                                                onClick={() => handleSizeChange(label)}
                                                sx={{
                                                    p: 1,
                                                    borderRadius: 2,
                                                    minWidth: 44,
                                                    border: isActive ? '2px solid' : '1px solid',
                                                    borderColor: isActive
                                                        ? 'primary.main'
                                                        : 'rgba(0,0,0,0.12)',
                                                    bgcolor: isActive
                                                        ? 'rgba(25,118,210,0.06)'
                                                        : 'transparent',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{ fontWeight: isActive ? 700 : 500 }}
                                                >
                                                    {label}
                                                </Typography>
                                            </Button>
                                        );
                                    })}
                                </Stack>
                                {errorSize && (
                                    <Typography variant="caption" color="error">
                                        {errorSize}
                                    </Typography>
                                )}
                            </Grid>
                        )}

                        {/* Add to Cart Button */}
                        <Grid item xs={12} sm={2} md={2} sx={{ width: '100%' }}>
                            <Button
                                variant="contained"
                                color="success"
                                size="large"
                                fullWidth
                                onClick={handleAddToCart}
                                disabled={isDisabled}
                                sx={{
                                    py: 1.4,
                                    fontWeight: 600,
                                    mt: { xs: 2, sm: 0 },
                                }}
                            >
                                ADD TO CART
                            </Button>
                        </Grid>
                    </Grid>
                </Box>)




                :
                (
                    <Box
                        position="fixed"
                        bottom={0}
                        left={0}
                        right={0}
                        bgcolor="#fff"
                        px={2}
                        py={2}
                        boxShadow={3}
                        zIndex={9999}
                        sx={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}
                    >
                        <Grid
                            container
                            alignItems="end"
                            spacing={2}
                            wrap="nowrap"
                            sx={{
                                overflowX: { xs: 'auto', sm: 'visible' },
                                flexWrap: { xs: 'nowrap', sm: 'wrap' },
                                pb: { xs: 1, sm: 0 },
                            }}
                        >
                            {/* Product image and name */}
                            <Grid
                                item
                                sx={{
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: { xs: 120, sm: 160 },
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    src={thumb}
                                    alt={product?.name}
                                    sx={{
                                        width: { xs: 50, sm: 60 },
                                        height: 'auto',
                                        borderRadius: 1,
                                        objectFit: 'contain',
                                    }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={700} noWrap>
                                        {product?.name}
                                    </Typography>
                                    <Typography variant="body2">
                                        {formatCurrencyNew(
                                            product?.price ||
                                            selectedVariant?.price ||
                                            product?.display_price ||
                                            '',
                                            websiteInfo?.currency_symbol || ''
                                        ) || ''}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Colors */}
                            {availableColors && (
                                <Grid
                                    item
                                    sx={{
                                        flexShrink: 0,
                                        minWidth: { xs: 120, sm: 160 },
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Stack direction="row" spacing={1}>
                                        {availableColors.map((color) => {
                                            const key = color.key ?? color.id ?? color.name?.toLowerCase();
                                            const isSelected = selectedColorKey === key;
                                            return (
                                                <Box
                                                    key={key}
                                                    onClick={() => handleColorChange(key)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',

                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            backgroundColor: color.hex_code,
                                                            backgroundImage: color.img
                                                                ? `url(${color.img})`
                                                                : undefined,
                                                            backgroundSize: 'cover',
                                                            borderRadius: 1,
                                                            border: isSelected
                                                                ? '3px solid rgba(25,118,210,1)'
                                                                : '1px solid rgba(0,0,0,0.12)',
                                                        }}
                                                    />
                                                    <Typography variant="caption" noWrap lineHeight={1.2}>
                                                        {color.name}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                </Grid>
                            )}

                            {/* Sizes */}
                            {availableSizesForSelectedColor() && (
                                <Grid
                                    item
                                    sx={{
                                        flexShrink: 0,
                                        minWidth: { xs: 160, sm: 200 },
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
                                        {availableSizesForSelectedColor().map((label) => {
                                            const isActive =
                                                selectedSizeLabel?.toString().toLowerCase() ===
                                                label.toString().toLowerCase();
                                            return (
                                                <Button
                                                    key={label}
                                                    onClick={() => handleSizeChange(label)}
                                                    sx={{
                                                        p: 0.5,
                                                        minWidth: 44,
                                                        borderRadius: 2,
                                                        border: isActive ? '2px solid' : '1px solid',
                                                        borderColor: isActive
                                                            ? 'primary.main'
                                                            : 'rgba(0,0,0,0.12)',
                                                        bgcolor: isActive
                                                            ? 'rgba(25,118,210,0.06)'
                                                            : 'transparent',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ fontWeight: isActive ? 700 : 500 }}
                                                    >
                                                        {label}
                                                    </Typography>
                                                </Button>
                                            );
                                        })}
                                    </Stack>
                                </Grid>
                            )}

                            {/* Add to Cart button (always at far right on desktop) */}
                            <Grid
                                item
                                sx={{
                                    ml: 'auto', // 👈 pushes button to far right on desktop
                                    flexShrink: 0,
                                    minWidth: { xs: '100%', sm: 160 },
                                    textAlign: 'right',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    fullWidth={{ xs: true, sm: false }}
                                    onClick={handleAddToCart}
                                    disabled={isDisabled}
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        fontWeight: 600,
                                    }}
                                >
                                    ADD TO CART
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>


                )}</>
    );
}
